import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { TOTALLED_COLUMNS } from "./managementReportConfig";

import robotoNormal from "./pdf/fonts/roboto-normal";
import robotoBold from "./pdf/fonts/roboto-bold";
const waitForRender = () => new Promise(r => setTimeout(r, 60));
/* ================= FONT REGISTER ================= */

function registerFonts(pdf) {
  pdf.addFileToVFS("Roboto-Regular.ttf", robotoNormal);
  pdf.addFont("Roboto-Regular.ttf", "Roboto", "normal");

  pdf.addFileToVFS("Roboto-Bold.ttf", robotoBold);
  pdf.addFont("Roboto-Bold.ttf", "Roboto", "bold");

  pdf.setFont("Roboto", "normal");
}

/* ================= MAIN EXPORT ================= */

const CHART_TITLES = {
  topWatched: "En Çok İzlenen 10 Program (Total TR)",
  topActiveTv: "En Çok İzlenen 10 Program (Ort. Aktif TV)",
  topSwitchIn: "En Çok Kanala Geçiş Yapılan 10 Program",
};

// Column definitions for the programme table, so a customer can leave out the
// ones they have no use for (or no permission for).
const TABLE_COLUMN_DEFS = {
  viewers: {
    head: "Toplam Türkiye İzleyici",
    cell: (r) => r.totalvalue.toLocaleString("tr-TR"),
  },
  switchIn: {
    head: "Kanala Giriş",
    cell: (r) => r.value.toLocaleString("tr-TR"),
  },
  avgActiveTv: {
    head: "Ort. Aktif TV",
    cell: (r) => (r.avgActiveTv ?? 0).toLocaleString("tr-TR"),
  },
  twitter: {
    head: ["Twitter Aralık", "Twitter İzleyici"],
    cell: (r) => [r.twitterTimeRange, r.twittervalue.toLocaleString("tr-TR")],
  },
  youtube: {
    head: ["YouTube Aralık", "YouTube İzleyici"],
    cell: (r) => [r.youtubeTimeRange, r.youtubevalue.toLocaleString("tr-TR")],
  },
};

const DEFAULT_CONFIG = {
  chart1: "topWatched",
  chart2: "topSwitchIn",
  tableEnabled: true,
  tableColumns: ["viewers", "switchIn", "avgActiveTv", "twitter", "youtube"],
  totals: ["daily", "monthly"],
  values: ["avgVideoConsumption"],
};

export async function exportManagementReportPdf({
  channelName,
  selectedDate,
  rows,
  totals,
  // Keyed by chart slot rather than passed one argument per chart, so a new
  // ranking is a new entry here and nothing else.
  charts = {},
  config = DEFAULT_CONFIG,
  onProgress = () => {}
}) {

  onProgress(5); // 🚀 export başladı
  await waitForRender();
  // Use a3 landscape for higher resolution
  const pdf = new jsPDF("landscape", "pt", "a3", true);
  registerFonts(pdf);

  const pageWidth = pdf.internal.pageSize.getWidth();
  const pageHeight = pdf.internal.pageSize.getHeight();

  // Sections are chosen by the customer, so the first one rendered must not
  // start with a page break and the numbering has to follow whatever is left in.
  let started = false;
  const startPage = () => {
    if (started) pdf.addPage();
    started = true;

    pdf.setFont("Roboto", "bold");
    pdf.setFontSize(38);
    pdf.setTextColor("#ED4333");
    pdf.text(
      `${channelName} - SMART TV & SOSYAL MEDYA İZLENMELERİ`,
      pageWidth / 2,
      80,
      { align: "center" }
    );
    pdf.setFont("Roboto", "normal");
    pdf.setFontSize(22);
    pdf.setTextColor("#111");
    pdf.text(selectedDate, pageWidth - 80, 120, { align: "right" });
  };

  const chartFor = (slot) => charts[slot];

  const chartSlots = [config.chart1, config.chart2].filter(
    (slot) => slot && slot !== "none" && CHART_TITLES[slot]
  );

  let progress = 5;
  for (const slot of chartSlots) {
    startPage();

    pdf.setFont("Roboto", "bold");
    pdf.setFontSize(28);
    pdf.setTextColor("#ED4333");
    pdf.text(CHART_TITLES[slot], pageWidth / 2, 180, { align: "center" });

    // Guarded on the canvas, not just the object: a destroyed Chart.js
    // instance keeps its shape but nulls its canvas, and toBase64Image then
    // throws instead of returning nothing.
    const chart = chartFor(slot);
    if (chart?.canvas) {
      const img = chart.toBase64Image("image/png", 2);
      pdf.addImage(img, "PNG", (pageWidth - 900) / 2, 210, 900, 600);
    }

    progress += 22;
    onProgress(progress);
    await waitForRender();
  }

  const wantsTable = config.tableEnabled !== false;
  const wantsTotals = (config.totals || []).length > 0;
  const wantsValues = (config.values || []).length > 0;

  if (wantsTable || wantsTotals || wantsValues) {
    startPage();
  }
  pdf.setFont("Roboto", "bold");
  pdf.setFontSize(38);
  pdf.setTextColor("#ED4333");
  pdf.text(
    `${channelName} - SMART TV & SOSYAL MEDYA İZLENMELERİ`,
    pageWidth / 2,
    80,
    { align: "center" }
  );
  pdf.setFont("Roboto", "normal");
  pdf.setFontSize(22);
  pdf.setTextColor("#111");
  pdf.text(selectedDate, pageWidth - 80, 120, { align: "right" });

  let tableEndY = 180;

  if (wantsTable) {
    const cols = (config.tableColumns || []).filter((c) => TABLE_COLUMN_DEFS[c]);

    const head = ["Program Saati", "Program"];
    cols.forEach((c) => {
      const h = TABLE_COLUMN_DEFS[c].head;
      Array.isArray(h) ? head.push(...h) : head.push(h);
    });

    autoTable(pdf, {
      startY: 180,
      margin: { left: 60, right: 60 },
      head: [head],
      body: rows.map((r) => {
        const line = [r.saat, r.program];
        cols.forEach((c) => {
          const cell = TABLE_COLUMN_DEFS[c].cell(r);
          Array.isArray(cell) ? line.push(...cell) : line.push(cell);
        });
        return line;
      }),
      styles: {
        font: "Roboto",
        fontSize: 16,
        cellPadding: 8,
        valign: "middle",
        lineColor: [0, 0, 0],
        lineWidth: 0.5
      },
      headStyles: {
        fillColor: [242, 242, 242],
        textColor: [20, 20, 20],
        fontStyle: "bold",
        fontSize: 18,
        halign: "center"
      }
    });

    tableEndY = pdf.lastAutoTable.finalY;
  }

  onProgress(75); // 📋 tablo hazır
  await waitForRender();
  let endY = tableEndY + 60;
  const margin = 80;
  const usableWidth = pageWidth - margin * 2;

  // Totals mirror the table's columns: no point printing a Twitter total when
  // the customer left the Twitter column out.
  const totalCols = (config.tableColumns || []).filter((c) =>
    TOTALLED_COLUMNS.includes(c)
  );
  const TOTAL_LABELS = {
    viewers: ["Toplam Türkiye İzleyici", "imp"],
    switchIn: ["Toplam Kanala Giriş", "switchIn"],
    // Deliberately not "Toplam": it is an average across the period, and
    // calling it a total would invite it to be read as a sum.
    avgActiveTv: ["Ort. Aktif TV", "avgActiveTv"],
    twitter: ["Toplam Twitter İzleyici", "twitter"],
    youtube: ["Toplam YouTube İzleyici", "youtube"],
  };

  const drawTotals = (period, suffix, y) => {
    const bucket = totals?.[period];
    if (!bucket) return;

    const width = usableWidth / Math.max(totalCols.length, 1);
    totalCols.forEach((c, i) => {
      const [label, key] = TOTAL_LABELS[c];
      pdf.text(
        `${label}${suffix}: ${(bucket[key] ?? 0).toLocaleString("tr-TR")}`,
        margin + width * i,
        y,
        { maxWidth: width - 12 }
      );
    });
  };

  if (wantsTotals && totalCols.length > 0) {
    pdf.setFont("Roboto", "bold");
    // Five totals across one line need smaller type; at 20pt the labels wrap
    // into each other's rows.
    pdf.setFontSize(totalCols.length > 3 ? 15 : 20);
    pdf.setTextColor("#ED4333");

    const periods = config.totals || [];

    if (periods.includes("daily")) {
      drawTotals("daily", "", endY);
      onProgress(90);
      await waitForRender();
      endY += 44;
    }

    if (periods.includes("monthly")) {
      pdf.setDrawColor(220);
      pdf.line(margin, endY - 18, pageWidth - margin, endY - 18);
      drawTotals("monthly", " Aylık", endY);
      endY += 44;
    }
  }


  if (wantsValues) {
    const values = config.values || [];

    pdf.setFont("Roboto", "bold");
    pdf.setFontSize(20);
    pdf.setTextColor("#ED4333");

    const avc = totals?.daily?.avgVideoConsumption;

    if (values.includes("avgVideoConsumption") && avc !== null && avc !== undefined) {
      pdf.text(
        `Ort. Kanalda Kalma: ${avc.toLocaleString("tr-TR")} dk`,
        margin,
        endY
      );
      endY += 44;
    }
  }

  pdf.setFontSize(16);
  pdf.setTextColor(150);
  pdf.text(
    `Sayfa ${pdf.internal.getNumberOfPages()}`,
    pageWidth - 120,
    pageHeight - 40
  );

  onProgress(100); // 💾 save öncesi
  await waitForRender();
  pdf.save(`${channelName}-${selectedDate}.pdf`);
}
